import { STAYCITY, WILDE } from "./themes";
import { BRANDS, determineBrandFromURL } from "../helpers/brand";
import { getStaticMapTheme } from "./getStaticMapTheme";
import { Loader } from "@googlemaps/js-api-loader";
import { breakpoints } from "../helpers/breakpoints";

export const googleMaps = () => {
  const key = window.GOOGLE_MAPS_KEY ?? process.env.GOOGLE_MAPS_KEY;

  /* || MAPS, CONTAINERS, MARKERS, POINTS OF INTEREST */
  const staticMap = document.querySelector<HTMLImageElement>("[js-map]");
  const mapLocations =
    document.querySelectorAll<HTMLElement>("[js-map-location]");
  const mapPOILocations = document.querySelectorAll<HTMLElement>(
    "[js-map-poi-location]"
  );
  const fullMapHandler = document.querySelector("[js-load-full-map");
  const fullMapContainer = document.querySelector<HTMLElement>("[js-full-map]");

  if (!staticMap || !key) {
    return;
  }

  /* || MAP PARAMETERS */
  const zoom = 14;
  const scale = 2;
  const markerScale = 2;
  const height = 300;

  /* On desktop, increase width */
  const width = window.innerWidth >= breakpoints.screen1024 ? 720 : 175;

  /* || BRANDING */
  /* Get brand theme */
  const brand = determineBrandFromURL();
  const brandTheme = getStaticMapTheme(
    brand === BRANDS.STAYCITY ? STAYCITY : WILDE
  );

  /* Get brand markers */
  const brandMarkerInternal =
    brand === BRANDS.STAYCITY
      ? staticMap.getAttribute("js-map-marker-sc")
      : staticMap.getAttribute("js-map-marker-wd");

  const brandMarkerExternal =
    brand === BRANDS.STAYCITY
      ? staticMap.getAttribute("js-map-marker-wd")
      : staticMap.getAttribute("js-map-marker-sc");

  /* || STATIC MAPS */
  const googleMapsStatic = () => {
    /* Loop through list of locations and append lat + lng to url string */
    let mapInternalLocationsString = "";
    let mapExternalLocationsString = "";

    mapLocations.forEach((location) => {
      /* Show different marker icon if for sister brand */
      const locationExternal = location.getAttribute("js-external");

      /* Append coordinates into either internal or external string (They have different markers) */
      if (locationExternal === "true") {
        mapExternalLocationsString = mapExternalLocationsString.concat(
          "|" + location.getAttribute("js-map-location")
        );
      } else {
        mapInternalLocationsString = mapInternalLocationsString.concat(
          "|" + location.getAttribute("js-map-location")
        );
      }
    });

    /* Render static map */
    staticMap.src = `https://maps.googleapis.com/maps/api/staticmap?&zoom=${zoom}&scale=${scale}&size=${width}x${height}&markers=scale:${markerScale}|icon:${brandMarkerInternal}${mapInternalLocationsString}&markers=scale:${markerScale}|icon:${brandMarkerExternal}${mapExternalLocationsString}${brandTheme}&key=${key}`;
  };

  /* || FULL MAP HANDLER */
  /* Hover state on static maps. On click, load full map */
  const googleMapsFullHandler = () => {
    if (!fullMapHandler) {
      return;
    }

    /* On mobile the full map should have two taps to load. First tap/click shows the hover state */
    staticMap.addEventListener("click", () => {
      if (window.innerWidth < breakpoints.screen1024) {
        fullMapHandler.classList.add("show-map-overlay");
      }
    });

    fullMapHandler.addEventListener("click", () => {
      /* Load full map, remove static map and overlay */
      googleMapsFull();
      staticMap.remove();
      fullMapHandler.remove();
    });
  };

  /* || FULL GOOGLE MAP */
  const googleMapsFull = async () => {
    if (!fullMapContainer) {
      return;
    }

    const loader = new Loader({
      apiKey: key,
    });

    /* Map options */
    const mapOptions = {
      center: {
        lat: 0,
        lng: 0,
      },
      zoom: zoom,
      streetViewControl: false,
      mapTypeControl: false,
      styles: brand === BRANDS.STAYCITY ? STAYCITY : WILDE,
    };

    /* Create map */
    const google = await loader.load();

    const map = new google.maps.Map(fullMapContainer, mapOptions);
    const bounds = new google.maps.LatLngBounds();

    /* Loop through properties and create markers */
    mapLocations.forEach((location) => {
      const coordinates = location.getAttribute("js-map-location")?.split(",");

      if (!coordinates) {
        return;
      }

      const lat = +coordinates[0];
      const lng = +coordinates[1];
      const label = location.getAttribute("js-map-name")!;
      const locationExternal = location.getAttribute("js-external");

      /* Show different marker icon if for other brand */
      const icon =
        locationExternal === "true" ? brandMarkerExternal : brandMarkerInternal;

      new google.maps.Marker({
        map,
        position: new google.maps.LatLng(lat, lng),
        icon: icon,
        label: {
          text: label,
          fontFamily: "",
          fontSize: "",
          color: "",
          className: "maps-marker-label margin--map-marker-label t-copy-tiny",
        },
      });
      bounds.extend(new google.maps.LatLng(lat, lng));
    });

    /* Marker for Points of Interest */
    /* Path is dummy svg data as it's a required field. Marker is transparent and never seen. Only label is shown */
    const svgMarker = {
      path: "M10 10",
      fillOpacity: 0,
      strokeWeight: 0,
    };

    /* Loop through points of interest and create markers */
    /* || POTENTIAL REFACTOR ||  lot of duplicated code with property markers */
    mapPOILocations.forEach((location) => {
      const coordinates = location
        .getAttribute("js-map-poi-location")
        ?.split(",");

      if (!coordinates) {
        return;
      }

      const lat = +coordinates[0];
      const lng = +coordinates[1];

      if (!lat || !lng) {
        return;
      }

      const label = location.getAttribute("js-map-poi-name")!;

      new google.maps.Marker({
        map,
        position: new google.maps.LatLng(lat, lng),
        icon: svgMarker,
        label: {
          text: label,
          fontFamily: "",
          fontSize: "",
          color: "",
          className: "maps-marker-label maps-marker-label--poi t-copy-tiny",
        },
      });
      bounds.extend(new google.maps.LatLng(lat, lng));
    });

    /* Zoom and pan map to center between all markers */
    map.fitBounds(bounds);
    map.panToBounds(bounds);

    google.maps.event.addListenerOnce(map, "bounds_changed", function () {
      this.setZoom(Math.min(this.getZoom(), zoom));
    });
  };

  /* || INIT */
  googleMapsStatic();
  googleMapsFullHandler();
};
